import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { ListChecks } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import MyTasksDialog, { SKIP_KEY } from './MyTasksDialog';

/**
 * Phase 27 — Task icon for the header.
 *  • Shows a red badge with the number of pending tasks for THIS user.
 *  • Opens the My Tasks modal on click.
 *  • Auto-opens once on first mount after login UNLESS the user ticked
 *    "Don't show again this session" (sessionStorage SKIP_KEY).
 *  • Shows a critical toast when any task is critical/overdue.
 */
export default function TaskIconButton({ compact = false }) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ total: 0, critical: 0, overdue: 0 });
  const [autoShown, setAutoShown] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/my-tasks`, { withCredentials: true });
      setSummary(r.data?.summary || {});
      return r.data?.summary || {};
    } catch (e) {
      return null;
    }
  }, [user]);

  // Initial load + light polling so the badge stays fresh (every 2 min)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    // Feb-2026 — never slam the My Tasks reminder on top of a user who
    // arrived via a deep-link (Approvals → /reports?focus=<id>, MR→PR chain
    // → /purchase-requests?highlight=<num>, etc.). If any of these params
    // exist we skip the auto-open so the target dialog is visible without
    // needing to close a reminder first.
    const dl = window.location.search || '';
    const isDeepLink = /[?&](focus|highlight|new)=/.test(dl);
    const init = async () => {
      const s = await fetchSummary();
      if (cancelled || !s) return;
      // Auto-show modal once per session if not dismissed AND not deep-linked
      if (!autoShown && !isDeepLink && !sessionStorage.getItem(SKIP_KEY) && s.total > 0) {
        setOpen(true);
        setAutoShown(true);
      }
      // Critical / overdue toast (still safe — non-blocking bottom-corner)
      const critical = (s.critical || 0) + (s.overdue || 0);
      if (critical > 0) {
        try {
          toast.warning(`You have ${critical} urgent task${critical === 1 ? '' : 's'} that need attention`, {
            duration: 6000,
            action: { label: 'View', onClick: () => setOpen(true) },
          });
        } catch { /* sonner not ready */ }
      }
    };
    init();
    const id = setInterval(fetchSummary, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, fetchSummary, autoShown]);

  if (!user) return null;

  const count = summary?.total || 0;
  const urgentCount = (summary?.critical || 0) + (summary?.overdue || 0);

  return (
    <>
      <Button
        data-testid={compact ? 'mobile-tasks-btn' : 'desktop-tasks-btn'}
        onClick={async () => { await fetchSummary(); setOpen(true); }}
        variant="ghost"
        size="sm"
        className="relative p-2"
        aria-label="My Tasks"
        title="My Tasks"
      >
        <ListChecks className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-foreground`} />
        {count > 0 && (
          <span
            data-testid="tasks-badge"
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-background ${
              urgentCount > 0 ? 'bg-rose-600 text-white' : 'bg-amber-500 text-slate-900'
            }`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>
      <MyTasksDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
